import type { ServiceRelationshipConfig, WorkspaceServiceRequestView } from "@closer/shared";
import {
  SERVICE_RELATIONSHIP_INITIATOR_NAMES,
  SERVICE_RELATIONSHIP_STATUS_NAMES,
} from "@closer/shared";
import { Badge, Button, Card, FormField, Select, Skeleton, useToast } from "@closer/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { StateView } from "../../components/StateView";
import { api, ApiRequestError } from "../../lib/apiClient";
import { useAuth } from "../../lib/authContext";
import { tenantKey } from "../../lib/tenantQuery";
import { resolveErrorState } from "../../lib/uiState";

/**
 * HİZMET İLİŞKİSİ — MÜVEKKİL TARAFI (CLOS-246 `[133]` · CLOS-248 `[134]`).
 *
 * ### ⚠ Kartın çekirdek kuralının EKRANDAKİ yarısı
 *
 * Büro kendini yetkilendiremiyor; kararı burası veriyor. Bu bölüm olmadan büronun talebi
 * ekranda hiçbir yere düşmüyordu — akış ancak API'dan tamamlanabiliyordu ve kural ürünün
 * içinde görünmez kalıyordu.
 *
 * ### ⚠ Kendi talebimize KARAR VEREMİYORUZ
 *
 * Müvekkil tarafının açtığı talepte onay/ret yok, "geri çek" var; kararı büro verir.
 * Sunucunun sözü de bu ("Kendi tarafınızın açtığı talep buradan karara bağlanamaz").
 *
 * ### ⚠ Askıdan dönüş askıyı KOYANDA
 *
 * `resumeApproval=suspender`: askıyı büro koyduysa bu ekran düğme değil açıklama
 * gösteriyor — sunucunun reddedeceği bir eylemi kullanıcıya denettirmek olurdu.
 *
 * ### ⚠ Talep AÇMA yüzeyi E-POSTAYLA geldi (CLOS-1105 · K1 · ADR-0067)
 *
 * Eskiden burada form yoktu ve gerekçesi *"hedef büro seçilemiyor"*du: sunucu yalnız
 * `lawFirmTenantId` kabul ediyordu, kullanıcıdan tenant kimliği istemek ise yasak
 * (R1 §3.3). `POST /api/workspace/service-requests` bu yüzden ekranı olmayan bir uçtu.
 *
 * Artık büro E-POSTAYLA çağrılıyor: kullanıcı zaten bildiği bir şeyi yazıyor, sunucu
 * adresten organizasyon ÇÖZMÜYOR ve hangi organizasyonun adına kabul edileceğine karşı
 * taraf kendi oturumunda karar veriyor. Adresin Closer'da kayıtlı olup olmadığı yanıttan
 * okunamıyor — form her durumda aynı sonucu gösteriyor (R3 §21.2).
 */
export function WorkspaceServiceRequests() {
  const { activeTenantId, can } = useAuth();
  const tenantId = activeTenantId!;
  const toast = useToast();
  const queryClient = useQueryClient();
  const [gerekce, setGerekce] = useState<Record<string, string>>({});

  const yetkili = can("organization.service");

  const anahtar = tenantKey(tenantId, "workspace", "hizmet-talepleri");
  const talepler = useQuery({
    enabled: yetkili,
    queryKey: anahtar,
    queryFn: async () =>
      (await api<WorkspaceServiceRequestView[]>("/api/workspace/service-requests", { tenantId }))
        .data,
  });

  const politika = useQuery({
    enabled: yetkili,
    queryKey: tenantKey(tenantId, "service-policy"),
    queryFn: async () =>
      (await api<ServiceRelationshipConfig>("/api/clients/service-policy", { tenantId })).data,
  });

  const yenile = () => queryClient.invalidateQueries({ queryKey: anahtar });

  /**
   * ⚠ KAPSAM VARSAYILANI POLİTİKADAN: sabit bir anahtar yazsaydık, kapsam sözlüğü
   * değiştiğinde form sunucunun tanımadığı bir değer göndermeye başlardı.
   */

  const komut = useMutation({
    mutationFn: (girdi: {
      relId: string;
      komut: "approve" | "reject" | "cancel" | "suspend" | "end" | "resume";
      reason?: string;
    }) =>
      api(`/api/workspace/service-requests/${girdi.relId}/${girdi.komut}`, {
        method: "POST",
        tenantId,
        body: JSON.stringify(girdi.reason ? { reason: girdi.reason } : {}),
      }),
    onSuccess: async (_veri, girdi) => {
      setGerekce({});
      await yenile();
      toast.success(
        girdi.komut === "approve"
          ? "Hizmet ilişkisi onaylandı"
          : girdi.komut === "reject"
            ? "Talep reddedildi"
            : girdi.komut === "cancel"
              ? "Talep geri çekildi"
              : girdi.komut === "suspend"
                ? "Hizmet ilişkisi askıya alındı"
                : girdi.komut === "resume"
                  ? "Hizmet ilişkisi yeniden başladı"
                  : "Hizmet ilişkisi sona erdi",
      );
    },
    onError: (hata: unknown) =>
      toast.error(hata instanceof ApiRequestError ? hata.message : "İşlem tamamlanamadı"),
  });

  if (!yetkili) return null;

  const kapsamAdi = (deger: string) =>
    politika.data?.scopeTypes.find((k) => k.key === deger)?.label ?? deger;
  const gerekceAdi = (deger: string | null, sozluk?: { key: string; label: string }[]) =>
    deger ? (sozluk?.find((g) => g.key === deger)?.label ?? deger) : "Bilinmeyen";

  return (
    <Card>
      <div className="flex flex-col gap-3">
        <h2 className="text-base font-semibold text-fg-heading">Hizmet ilişkileri</h2>

        <p className="text-xs text-fg-soft">
          Bir hukuk bürosu sizin adınıza hizmet vermek için talep açtığında karar sizindir. Onay,
          büroya kayıtlarınıza erişim vermez; erişim her kayıt için ayrıca verilir.
        </p>

        {/*
          ⚠ "HUKUK BÜROSU EKLE" FORMU KALDIRILDI (CLOS-1122 / CLOS-1144, Halit kararı S6):
          ilişkiyi BÜRO başlatır, müvekkil tarafı yalnız kabul/ret eder. Müvekkilin büro
          çağırma yolu (CLOS-1105) supersede edildi; ucu da silindi.
        */}

        {talepler.isPending ? (
          <div role="status" aria-label="Hizmet talepleri yükleniyor">
            <Skeleton lines={2} />
          </div>
        ) : talepler.error ? (
          <StateView
            state={resolveErrorState(talepler.error, { idempotent: true })}
            onRetry={() => void talepler.refetch()}
          />
        ) : (talepler.data ?? []).length === 0 ? (
          <p className="text-sm text-fg-soft">Bekleyen ya da kurulmuş bir hizmet ilişkisi yok.</p>
        ) : (
          <ul className="flex flex-col gap-2">
            {(talepler.data ?? []).map((talep) => (
              <li
                key={talep.id}
                className="flex flex-col gap-2 rounded-md border border-border-default p-3"
              >
                <div className="flex flex-wrap items-center gap-2 text-sm">
                  {/* ⚠ Büro henüz seçilmemişse satır ADRESLE anlatılıyor: "bilinmiyor" yazmak
                      gönderilen daveti kayıp gösterirdi. */}
                  <span className="font-medium text-fg-strong">
                    {talep.lawFirmName ?? talep.targetEmail}
                  </span>
                  <Badge tone={talep.status === "active" ? "success" : "neutral"}>
                    {SERVICE_RELATIONSHIP_STATUS_NAMES[talep.status]}
                  </Badge>
                  <span className="text-xs text-fg-soft">{kapsamAdi(talep.scope)}</span>
                  <span className="text-xs text-fg-soft">
                    {SERVICE_RELATIONSHIP_INITIATOR_NAMES[talep.initiatedBy]}
                  </span>
                </div>

                {talep.scopeDetail ? <p className="text-sm text-fg">{talep.scopeDetail}</p> : null}

                {/* ⚠ Karar YALNIZ büronun açtığı bekleyen talepte. */}
                {talep.status === "pending" && talep.initiatedBy === "law_firm" ? (
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-xs text-fg-soft">
                      {new Date(talep.requestExpiresAt).toLocaleDateString("tr-TR")} tarihine kadar
                      karar verebilirsiniz
                    </span>
                    <Button
                      type="button"
                      size="sm"
                      className="ml-auto"
                      onClick={() => komut.mutate({ relId: talep.id, komut: "approve" })}
                    >
                      Onayla
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() => komut.mutate({ relId: talep.id, komut: "reject" })}
                    >
                      Reddet
                    </Button>
                  </div>
                ) : null}

                {/* ⚠ Kendi talebimizde karar değil GERİ ÇEKME var. */}
                {talep.status === "pending" && talep.initiatedBy === "client" ? (
                  <div className="flex justify-end">
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() => komut.mutate({ relId: talep.id, komut: "cancel" })}
                    >
                      Talebi geri çek
                    </Button>
                  </div>
                ) : null}

                {/* ⚠ Askıdan dönüş askıyı KOYANDA. */}
                {talep.status === "suspended" ? (
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-xs text-fg-soft">
                      {gerekceAdi(talep.suspendReason, politika.data?.suspendReasons)} gerekçesiyle
                      askıda
                    </span>
                    {talep.suspendedBy === "client" ? (
                      <Button
                        type="button"
                        size="sm"
                        variant="secondary"
                        onClick={() => komut.mutate({ relId: talep.id, komut: "resume" })}
                      >
                        Askıdan çıkar
                      </Button>
                    ) : (
                      <span className="text-xs text-fg-soft">
                        Askıyı büro koydu; askıdan çıkarma da onun kararı.
                      </span>
                    )}
                  </div>
                ) : null}

                {talep.status === "active" ? (
                  <div className="flex flex-wrap items-end gap-2">
                    <FormField label="Askı gerekçesi">
                      <Select
                        aria-label={`${talep.lawFirmName} askı gerekçesi`}
                        value={gerekce[`${talep.id}-suspend`] ?? ""}
                        onChange={(olay) =>
                          setGerekce((onceki) => ({
                            ...onceki,
                            [`${talep.id}-suspend`]: olay.target.value,
                          }))
                        }
                      >
                        <option value="">Seçiniz</option>
                        {(politika.data?.suspendReasons ?? []).map((g) => (
                          <option key={g.key} value={g.key}>
                            {g.label}
                          </option>
                        ))}
                      </Select>
                    </FormField>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      disabled={!gerekce[`${talep.id}-suspend`]}
                      onClick={() =>
                        komut.mutate({
                          relId: talep.id,
                          komut: "suspend",
                          reason: gerekce[`${talep.id}-suspend`],
                        })
                      }
                    >
                      Askıya al
                    </Button>
                  </div>
                ) : null}

                {talep.status === "active" || talep.status === "suspended" ? (
                  <div className="flex flex-wrap items-end gap-2">
                    <FormField label="Sonlandırma gerekçesi">
                      <Select
                        aria-label={`${talep.lawFirmName} sonlandırma gerekçesi`}
                        value={gerekce[`${talep.id}-end`] ?? ""}
                        onChange={(olay) =>
                          setGerekce((onceki) => ({
                            ...onceki,
                            [`${talep.id}-end`]: olay.target.value,
                          }))
                        }
                      >
                        <option value="">Seçiniz</option>
                        {(politika.data?.endReasons ?? []).map((g) => (
                          <option key={g.key} value={g.key}>
                            {g.label}
                          </option>
                        ))}
                      </Select>
                    </FormField>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      disabled={!gerekce[`${talep.id}-end`]}
                      onClick={() =>
                        komut.mutate({
                          relId: talep.id,
                          komut: "end",
                          reason: gerekce[`${talep.id}-end`],
                        })
                      }
                    >
                      Hizmeti sonlandır
                    </Button>
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </div>
    </Card>
  );
}
